import { recordCheckoutHandoffEvent } from './api';
import { HANDOFF_DECISION, HANDOFF_REASON } from './cfHandoff';

/**
 * Story 3.16 AC5 — ask-outcome recording (client half).
 *
 * ⚠️ APPEND-ONLY TELEMETRY, NOT A LEDGER. Nothing reads these rows to make a
 * decision and no invariant depends on them. Contrast `cf_order_claims` and
 * `slot_claim_tokens`, which ARE anchors (migrations 077 and 079: "two anchors
 * for two different invariants — do not merge them").
 *
 * AC5.5 — EMISSION IS FIRE-AND-FORGET. A recording failure must never block a
 * navigation or the modal. This mirrors the `pendingDraftId` best-effort
 * posture at EditListingPage.js:108-122: the promise is deliberately not
 * returned to the caller and its rejection is swallowed.
 *
 * AC5.4 — delivery follows the search-events proxy SHAPE
 * (`server/api/search-events.js` → tph-api, fire-and-forget, graceful 503 when
 * unprovisioned, upstream error bodies never relayed) with ONE DELIBERATE
 * DIVERGENCE: that proxy authenticates with the SERVICE token, which would make
 * `user_uuid` a client-asserted, spoofable body field. This record is
 * money-adjacent, so the server proxy forwards the CUSTOMER token instead and
 * tph-api stamps `user_uuid` from it. THE BODY NEVER CARRIES AN IDENTITY —
 * there is no `user_uuid` field below, on purpose, and RP-9 fences it.
 */

/** AC5.1's outcome vocabulary. */
export const ASK_OUTCOME = Object.freeze({
  SHOWN: 'shown',
  USE_EXISTING: 'use_existing',
  BUY_ANOTHER: 'buy_another',
  // AC5.3 — `dismissed` is its OWN outcome, never folded into `shown`. A high
  // dismiss rate means the copy is failing, and that signal is invisible if
  // dismiss is counted as an impression.
  DISMISSED: 'dismissed',
  BLOCKED: 'blocked',
  // ⚠️ SIXTH VALUE, and NOT one of AC5.1's five ask-screen resolutions.
  // It exists solely to carry AC5.6.3's [[#592]] discriminator: a gate
  // resolution where no screen was rendered at all. See recordAskOutcome.
  PROCEEDED: 'proceeded',
});

/**
 * FR-3 — the reasons whose `shown` row triggers an operator Slack alert in
 * tph-api (`AC4_ARM_REASONS` in src/routes/checkoutHandoffEvents.js). Only
 * these are worth a retry; everything else is analytics.
 *
 * ---------------------------------------------------------------------------
 * 🔴 RE-REVIEW ROUND 3, RR-1 — THIS COMMENT USED TO CLAIM A FENCE THAT DID NOT
 * EXIST. Read the correction before trusting anything below it.
 * ---------------------------------------------------------------------------
 * It said: *"this is pinned by a test rather than by the type system."* It was
 * not. `grep -rn ALERT_BEARING_REASONS src/ --include=*.test.js` returned ZERO
 * matches — the constant appeared exactly twice in the repo, its own
 * declaration and its own single use.
 *
 * ⚠️ STATED PLAINLY, SO THE NEXT READER IS NOT FOOLED TWICE:
 *
 *   **These two lists are a HAND-COPY across two repositories, and NOTHING
 *   ENFORCES THAT THEY AGREE.** The marketplace cannot import from tph-api, and
 *   no cross-repo mechanism exists in either direction. A cross-repo vocabulary
 *   pin is a real want, is now requested three times over (this,
 *   `PAID_NOT_SPENDABLE_STATUSES` per FR-N5, and `REASONS` itself), and is
 *   DEFERRED as a ledger candidate — see story §15.3's FR-N5 deferral. The
 *   deferral is sound; asserting it was already done was not.
 *
 * What IS fenced, in askOutcome.test.js, is the half that is assertable from
 * inside this repo: every entry here is a real `HANDOFF_REASON` value (catching
 * the typo that would silently never match), and the set is exactly the two
 * documented arms, so widening it cannot happen invisibly in a diff. What is
 * NOT fenced is agreement with tph-api. If someone changes `AC4_ARM_REASONS`
 * over there — SF-2's open question about Arm A is exactly such a change — this
 * list does not follow, no test reds, and the armed row silently loses the one
 * retry FR-3 exists to give it.
 *
 * ⚠️ Same class as FR-B1's doctrine item, one level up: FR-B1 was a safety
 * state depending on a resource; this was a safety state whose GUARD claimed
 * coverage it did not have. A comment that overstates its own fence is how the
 * next reader stops checking.
 */
export const ALERT_BEARING_REASONS = Object.freeze([
  HANDOFF_REASON.ARM_A_MIRROR_ZERO_AUTHORITY_SLOT,
  HANDOFF_REASON.ARM_B_PAID_NOT_SPENDABLE,
]);

/** Deferred so the retry does not simply re-hit the same transient failure. */
export const ALERT_RETRY_DELAY_MS = 1500;

/**
 * Record one gate resolution. Fire-and-forget; returns nothing.
 *
 * WHAT IS RECORDED, AND WHAT DELIBERATELY IS NOT:
 *   - every ask-screen resolution (the five AC5.1 outcomes), and
 *   - the [[#592]] inverse disagreement on the proceed path.
 * A plain `proceed` with nothing surprising about it writes NO ROW. Every
 * logged-in seller clicking a landing door would otherwise generate one, which
 * would bury the signal these counts exist to carry under ordinary traffic.
 *
 * @param {Object} args
 * @param {string} args.outcome one of ASK_OUTCOME
 * @param {Object} args.handoff the resolved gate result
 */
export const recordAskOutcome = ({ outcome, handoff } = {}) => {
  if (!handoff || !outcome) {
    return;
  }

  const isInverse = handoff.reason === HANDOFF_REASON.MIRROR_POSITIVE_AUTHORITY_EMPTY;
  const isProceed = handoff.decision === HANDOFF_DECISION.PROCEED;

  // 🔴 RE-REVIEW ROUND 3, RR-2 — `gate_threw` IS RECORDED ON THE PROCEED LEG TOO.
  //
  // RR-2 made a gate throw with no mirror evidence PROCEED rather than ask
  // (matching the gate's own posture — see CheckoutHandoffLink's catch). That
  // change would otherwise have silently deleted FR-5's entire contribution:
  // an ordinary proceed writes no row, so the most common gate-throw case —
  // the one where the mirror reads zero, i.e. where we know the least — would
  // have become invisible in the AC5 rows.
  //
  // `gate_threw` should be ~NEVER. `cfHandoff.js` says so at the constant: "if
  // it appears in the AC5 rows at all, something in the gate is broken." A
  // reason that means "our own code faulted" is exactly the surprising kind of
  // proceed this filter exists to let through, on the same footing as the
  // [[#592]] inverse. Suppressing it would leave a broken gate with no signal.
  //
  // ⚠️ This does NOT widen ordinary traffic: `no_slot` and `not_authenticated`
  // proceeds still write nothing, which is the whole point of the filter.
  const isGateFault = handoff.reason === HANDOFF_REASON.GATE_THREW;

  // AC5.6.3 — instrument the inverse, do NOT gate on it. This is the only way
  // to learn whether the shape survives Story 13.5 closing.
  if (isProceed && !isInverse && !isGateFault) {
    return;
  }

  const body = {
    outcome: isProceed ? ASK_OUTCOME.PROCEEDED : outcome,
    // The discriminator. `mirror_positive_authority_empty` IS the #592 marker.
    reason: handoff.reason,
    idle_slots: handoff.idleSlots || 0,
    tier_breakdown: (handoff.tierBreakdown || []).map(t => ({
      tier: t.tier,
      slots: Number(t.slots) || 0,
    })),
    paid_not_spendable_statuses: (handoff.paidNotSpendable || []).map(r => r.status),
    // ⚠️ NO user_uuid. NO dollar amounts. NO PII. The server stamps identity
    // from the customer token; a uuid here would be spoofable (RP-9).
  };

  // 🔴 RE-REVIEW FR-3 — THE ARMED ROW GETS ONE RETRY. NOTHING ELSE DOES.
  //
  // SF-1 made the Slack alert fire once per FIRING by keying it to the `shown`
  // row. That fixed a real 2-3x fan-out, but it also turned N chances of
  // alerting into exactly ONE — and that one is a fire-and-forget POST whose
  // every rejection is swallowed below. Lose it (503 when unprovisioned, a 401
  // on the very expired-cookie path B-3 made reachable, an ad blocker, a closed
  // tab) and a seller we can SEE was about to be charged twice generates no
  // alert at all, forever. The module's own doctrine — "a missed alert is LIVE
  // CUSTOMER HARM; a duplicate is noise" — was running backwards.
  //
  // So the row that carries an alert, and only that row, is retried once.
  //
  // ⚠️ THIS IS NOT THE DEDUP MARKER AC4.5 DECLINED, and it is not a retry
  // queue: no DDL, no persistence, no scheduler, one extra attempt in the same
  // tab session. A duplicate delivery is explicitly acceptable here (AC4.5
  // ratified duplicates as noise); a lost one is not. Ordinary telemetry rows
  // are NOT retried — they are analytics, and doubling their traffic to protect
  // a count nobody reads in real time would be the wrong trade.
  const isArmedFiring =
    outcome === ASK_OUTCOME.SHOWN && ALERT_BEARING_REASONS.includes(handoff.reason);

  const attempt = retriesLeft => {
    try {
      const p = recordCheckoutHandoffEvent(body);
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          if (retriesLeft > 0) {
            // Deferred, not immediate: an instant re-POST would just re-hit
            // whatever transient condition rejected the first one.
            setTimeout(() => attempt(retriesLeft - 1), ALERT_RETRY_DELAY_MS);
          }
        });
      }
    } catch (e) {
      // A synchronous throw is a broken transport, not a transient failure —
      // retrying it would fail identically. Swallowed, as before.
    }
  };

  attempt(isArmedFiring ? 1 : 0);
};
